import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, SubHeader } from 'vetrf-ui';
import { InfoTable } from 'components/InfoTable';
import { ConfirmModal } from 'components/ConfirmModal';
import { RemoveModal } from 'components/RemoveModal';
import { UserData } from 'components/UserDataContext/types';
import { EntryType } from 'components/registry/types/api';
import { OrderingWithSignatureModal } from 'components/OrderingWithSignatureModal/OrderingWithSignatureModal';
import { SigningPersonDictOption } from 'api/dict/params';
import { ErulRegistrationCodes } from 'api/erul/types';
import { ElkEpguRequestInfoDTO } from 'api/elkEpgu/types';
import { getTooltipForErul } from 'api/erul/utils';
import { erulApi } from 'api/erul/erulApi';
import { elkEpguApi } from 'api/elkEpgu';
import classNames from 'classnames';
import { feedAdditiveApi } from '../../../../../../api';
import { ViewFormType } from '../../../../../../api/params';
import { FunctionPermissions } from '../../../../../../pages/types';
import { ProducersList } from './components/ProducerList';
import { InformationTable } from './types';
import {
  developerBlockColumns,
  epguRegistrationBlockColumn,
  erulBlockColumns,
  erulWithStatusBlockColumns,
  getGMOBlockColumns,
  mainBlockColumns,
  requestBlockColumns,
  stateBlockColumns,
} from './consts';

type InformationProps = {
  data: ViewFormType;
  cardLink: string;
  registryLink: string;
  removeMethod: (id: string) => void;
  updateData: () => void;
  permissions: FunctionPermissions;
  entryType: EntryType;
  userData: UserData | null;
};

export const Information = ({
  data,
  cardLink,
  removeMethod,
  updateData,
  registryLink,
  permissions,
  entryType,
  userData,
}: InformationProps) => {
  const navigate = useNavigate();
  const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false);
  const [isOrderingModalOpen, setIsOrderingModalOpen] = useState(false);
  const [isErulModalOpen, setIsErulModalOpen] = useState(false);
  const [isElkModalOpen, setIsElkModalOpen] = useState(false);
  const [elkRequestInfo, setElkRequestInfo] = useState<ElkEpguRequestInfoDTO>();
  const [error, setError] = useState('');

  const openOrderingModal = () => {
    setIsOrderingModalOpen(true);
  };

  const closeOrderingModal = () => {
    setIsOrderingModalOpen(false);
  };

  const openRemoveModal = () => {
    setIsRemoveCardModalOpen(true);
  };

  const closeRemoveModal = () => {
    setIsRemoveCardModalOpen(false);
  };

  const openErulModal = () => {
    setIsErulModalOpen(true);
  };

  const closeErulModal = () => {
    setIsErulModalOpen(false);
  };

  const openElkEpguModal = () => {
    setIsElkModalOpen(true);
  };

  const closeElkEpguModal = () => {
    setIsElkModalOpen(false);
  };

  const onRemoveCard = async () => {
    await removeMethod(data.id);
    closeRemoveModal();
    navigate(registryLink);
  };

  const createOrderingWithSignature = (signingPerson: SigningPersonDictOption) => {
    closeOrderingModal();
    const sendData = {
      ...signingPerson,
      registryEntryId: data.id,
      signed: false,
    };
    feedAdditiveApi.getFeedAdditivePdf(sendData);
  };

  const createOrdering = () => {
    const sendData = {
      registryEntryId: data.id,
      signed: true,
    };
    feedAdditiveApi.getFeedAdditivePdf(sendData);
  };

  const filterColumns = (columns: InformationTable) =>
    columns.filter((item) => {
      if (item.access) {
        return permissions.edit;
      }
      if (
        item.isRegisteredRequest &&
        item.mode.includes(EntryType.requests) &&
        entryType === EntryType.requests
      ) {
        return !!userData;
      }
      if (
        item.isRegisteredRecord &&
        item.mode.includes(EntryType.registry) &&
        entryType === EntryType.registry
      ) {
        return !!userData;
      }
      if (item.isUserData) {
        return !!userData;
      }
      return (
        (item.mode.includes(EntryType.registry) && entryType === EntryType.registry) ||
        (item.mode.includes(EntryType.requests) && entryType === EntryType.requests)
      );
    });

  const mainBlockData = [
    {
      item: {
        ...data.product,
      },
    },
  ];

  const GMOBlockData = [
    {
      item: {
        gmoList: data.product.gmoList,
      },
    },
  ];

  const developerBlockData = [
    {
      item: {
        addressVendor: data.product.addressVendor,
        vendor: data.product.vendor,
        vendorType: data.product.vendorType,
        vendorCountry: data.product.vendorCountry,
        vendorAddressText: data.product.vendorAddressText || data.product?.addressVendor?.address,
        vendorEnteredManually: data.product.vendorEnteredManually,
        vendorNotLinkedWithCerberus: data.product.vendorNotLinkedWithCerberus,
        vendorIncorrectCerberusLink: data.product.vendorIncorrectCerberusLink,
      },
    },
  ];

  const stateBlockData = [
    {
      item: {
        series: data.series,
        regNumber: data.regNumber,
        registryStatus: data.registryStatus.name,
        requestDate: data.requestDate,
        startValidityDate: data.startValidityDate,
        endValidityDate: data.endValidityDate,
      },
    },
  ];

  const requestBlockData = [
    {
      item: {
        requestRegNumber: data.requestRegNumber,
        requestRegDate: data.requestRegDate,
        requestMethod: data.requestMethod,
        epguRegistration: data.epguRegistrationList?.length
          ? data.epguRegistrationList[data.epguRegistrationList.length - 1]
          : null,
      },
    },
  ];

  const erulBlockData = [
    {
      item: {
        erulRegNumber: data.erulRegNumber,
        erulRegDate: data.erulRegDate,
        erulManualChanged: data.erulManualChanged,
        erulRegistration: data.erulRegistration,
      },
    },
  ];

  const getErulFile = async () => {
    setError('');
    erulApi
      .downloadFeedFileForErul(data.id)
      .then()
      .catch(async (err) => {
        const errorData = JSON.parse(await err.response.data.text());
        setError(errorData.message);
      });
  };

  const sendToErul = async () => {
    closeErulModal();
    setError('');
    await erulApi
      .sendFeedToErul(data.id)
      .then(() => updateData())
      .catch((err) => setError(err.response.data.message));
  };

  const registerToEpgu = async () => {
    closeElkEpguModal();
    setError('');
    await elkEpguApi
      .registerFeed(data.id!, elkRequestInfo?.requestType?.id)
      .then(() => updateData())
      .catch((err) => setError(err.response.data.message));
  };

  useEffect(() => {
    if (permissions.elk) {
      elkEpguApi.getFeedRequestInfo(data.id!).then(setElkRequestInfo);
    }
  }, [permissions.elk]);

  return (
    <>
      {error && (
        <Alert closable messageType="danger">
          {error}
        </Alert>
      )}
      <SubHeader
        header="Информация"
        icon="info"
        iconColor="orange"
        iconSize={120}
        level={4}
        toolbar={
          <div className="info-toolbar">
            <div
              className={classNames(
                (permissions.edit || permissions.duplicate) && 'card-btn-group'
              )}
            >
              {permissions.edit &&
                data.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_NEW &&
                data.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_IN_PROGRESS && (
                  <Button
                    icon="pencil"
                    iconColor="green"
                    iconSize={150}
                    onClick={() => {
                      navigate(`${cardLink}/edit`);
                    }}
                    onlyIcon
                    tooltip="Редактировать"
                  />
                )}
              {permissions.duplicate && (
                <Button
                  icon="copy"
                  iconColor="orange"
                  iconSize={150}
                  onClick={() => {
                    navigate('/feed/addRequest', {
                      state: { duplicateId: data.id, backLink: cardLink },
                    });
                  }}
                  onlyIcon
                  tooltip="Дублировать"
                />
              )}
            </div>
            {permissions.edit && entryType === EntryType.registry && (
              <div className="card-btn-group">
                <Button
                  icon=" orderingWithSignature"
                  iconColor="orange"
                  iconSize={150}
                  onClick={createOrdering}
                  onlyIcon
                  tooltip="Формирование выписки с ЭП"
                />
                <Button
                  icon=" ordering"
                  iconColor="orange"
                  iconSize={150}
                  onClick={openOrderingModal}
                  onlyIcon
                  tooltip="Формирование выписки без ЭП"
                />
              </div>
            )}
            {permissions.erul && entryType === EntryType.registry && (
              <div className="card-btn-group">
                <Button
                  icon="file-code-o"
                  iconColor="orange"
                  iconSize={150}
                  onClick={getErulFile}
                  onlyIcon
                  tooltip="Выгрузить данные для регистрации в ЕРУЛ вручную"
                />
                <Button
                  disabled={
                    data.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_NEW ||
                    data.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_IN_PROGRESS
                  }
                  icon="send-o"
                  iconColor="orange"
                  iconSize={150}
                  onClick={openErulModal}
                  onlyIcon
                  tooltip={getTooltipForErul(data.erulRegistration)}
                />
              </div>
            )}
            {permissions.elk && (
              <div className="card-btn-group">
                <Button
                  disabled={!elkRequestInfo?.operationApplicable}
                  icon="id-card"
                  iconColor="blue"
                  iconSize={150}
                  onClick={openElkEpguModal}
                  onlyIcon
                  tooltip="Передача в ЕЛК ЕПГУ статуса заявления о регистрации объекта"
                />
              </div>
            )}
            {permissions.remove && (
              <Button
                icon="trash"
                iconColor="red"
                iconSize={150}
                onClick={openRemoveModal}
                onlyIcon
                tooltip="Удалить"
              />
            )}
          </div>
        }
      />
      <div>
        <InfoTable itemList={mainBlockData} rows={filterColumns(mainBlockColumns)} />
        <InfoTable itemList={GMOBlockData} rows={getGMOBlockColumns(entryType)} />
        <InfoTable itemList={developerBlockData} rows={developerBlockColumns} />
        <ProducersList producers={data.product.producerList} />
        <InfoTable itemList={stateBlockData} rows={filterColumns(stateBlockColumns)} />
        <InfoTable
          itemList={requestBlockData}
          rows={filterColumns(
            permissions.elk
              ? [...requestBlockColumns, epguRegistrationBlockColumn]
              : requestBlockColumns
          )}
        />
        <InfoTable
          itemList={erulBlockData}
          rows={filterColumns(permissions.erul ? erulWithStatusBlockColumns : erulBlockColumns)}
        />

        {isOrderingModalOpen && (
          <OrderingWithSignatureModal
            onClose={closeOrderingModal}
            onSubmit={createOrderingWithSignature}
          />
        )}

        {isRemoveCardModalOpen && (
          <RemoveModal
            onClose={closeRemoveModal}
            onRemove={onRemoveCard}
            modalContent="Вы уверены, что хотите удалить запись?"
          />
        )}
        {isErulModalOpen && (
          <ConfirmModal
            onClose={closeErulModal}
            onConfirm={sendToErul}
            modalContent="Вы уверены, что хотите отправить запрос на регистрацию в ЕРУЛ?"
          />
        )}
        {isElkModalOpen && (
          <ConfirmModal
            onClose={closeElkEpguModal}
            onConfirm={registerToEpgu}
            modalContent={`Вы уверены, что хотите выполнить по заявлению ${elkRequestInfo?.requestNumber} от ${elkRequestInfo?.requestDate} запрос "${elkRequestInfo?.requestType?.name}"?`}
          />
        )}
      </div>
    </>
  );
};
